<template>
    <div>
        <!-- Expedia Webhook Subscription Management Card -->
        <div class="card card-default mb-4">
            <div class="card-header">
                {{ __('Subscriptions') }}
            </div>
            <div class="card-body">

                <div class="ml-3 form-group">
                    <label>{{ __('Available Event Subscriptions') }}</label>
                    <div class="form-check" v-for="event in availableEvents" :key="event">
                        <input class="form-check-input my-auto" type="checkbox" :id="`event-${event}`" :value="event"
                            v-model="selectedEvents">
                        <label class="form-check-label" :for="`event-${event}`">
                            {{ event }}
                        </label>
                    </div>
                </div>

                <button class="btn btn-primary mt-2" @click="updateSubscriptions">
                    {{ __('Update Subscription') }}
                </button>
            </div>
        </div>

        <!-- Team & Expedia Property ID Associations Card -->
        <div class="card card-default">
            <div class="card-header">
                {{ __('Propery Associations') }}
            </div>
            <div class="card-body">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>{{ __('Actions') }}</th>
                            <th>{{ __('Team') }}</th>
                            <th>{{ __('Property ID') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(assoc, index) in associations" :key="assoc.team_id">
                            <td>
                                <button class="btn btn-danger btn-sm" @click="removeAssociation(assoc.team_id)">
                                    <i class="fa fa-trash"></i>
                                </button>
                            </td>
                            <td>{{ groupTeams[assoc.team_id]?.name ?? assoc.team_id }}</td>
                            <td>{{ assoc.remote_id }}</td>
                        </tr>
                        <tr>
                            <td>
                                <button class="btn btn-success btn-sm" @click="saveAssociation">
                                    <i class="fa fa-plus"></i>
                                </button>
                            </td>
                            <td>
                                <input type="text" class="form-control" v-model="teamSearch"
                                    :placeholder="__('Search by property name')">
                                <ul class="list-group" v-if="filteredTeams.length">
                                    <li class="list-group-item" v-for="team in filteredTeams" :key="team.team_id"
                                        @click="selectTeam(team)">
                                        {{ team.team.name }}
                                    </li>
                                </ul>
                            </td>
                            <td>
                                <input type="text" class="form-control" v-model="propertyId"
                                    placeholder="Expedia Property ID">
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import difference from 'lodash/difference';

export default {
    props: {
        connection: {
            type: Object,
            required: true
        },
        groupId: {
            required: false,
        },
        teamId: {
            required: false,
        }
    },
    mounted() {
        this.fetchAssociations();
    },
    data() {
        return {
            availableEvents: [
                'ReviewsApproved',
                'ReviewsManagementResponseRejected',
                'ReviewsManagementResponseApproved',
                'GuestReviewSubmitted'
            ],
            selectedEvents: this.connection.webhooks[0]?.config?.event_types || [],
            propertyId: '',
            teamSearch: '',
            selectedTeam: null,
            associations: []
        };
    },
    computed: {
        filteredTeams() {

            if (!this.teamSearch.length) {
                return [];
            }

            const lowerSearch = this.teamSearch.toLowerCase();
            const existingIds = this.associations.map(a => a.team_id);
            return this.spark.state.currentGroup.group_teams.filter(group_team =>
                group_team.team.name.toLowerCase().includes(lowerSearch) && !existingIds.includes(group_team.team_id)
            );
        },
        groupTeams() {
            return this.spark.state.currentGroup.group_teams.reduce((acc, group_team) => {
                acc[group_team.team.id] = group_team.team;
                return acc;
            }, {});
        }
    },
    methods: {
        async updateSubscriptions() {
            const webhookId = this.connection.webhooks[0]?.id;
            const currentEvents = this.connection.webhooks[0]?.config?.event_types || [];

            const addedEvents = difference(this.selectedEvents, currentEvents);
            const removedEvents = difference(currentEvents, this.selectedEvents);

            try {
                if (addedEvents.length || removedEvents.length) {
                    await axios.put(`/api/group-integrations/${this.groupId}/${this.connection.id}/webhooks/${webhookId}`, {
                        connection_id: this.connection.id,
                        subscribe: addedEvents,
                        unsubscribe: removedEvents,
                    });

                    this.$emit('update-connection')

                }
            } catch (error) {
                console.error('Error updating subscriptions:', error);
            }
        },
        selectTeam(team) {
            this.selectedTeam = team;
            this.teamSearch = '';
            this.teamSearch = team.team.name;
        },
        async saveAssociation() {
            if (this.selectedTeam && this.propertyId) {
                const newAssociation = {
                    team_id: this.selectedTeam.team.id,
                    remote_id: this.propertyId
                };

                this.associations.push(newAssociation);

                try {
                    await axios.post(`/api/group-integrations/${this.groupId}/${this.connection.id}/associations`, { associations: this.associations });
                } catch (error) {
                    console.error('Error saving association:', error);
                }

                this.selectedTeam = null;
                this.teamSearch = '';
                this.propertyId = '';
            }
        },
        async removeAssociation(team_id) {
            this.associations = this.associations.filter(a => a.team_id !== team_id);

            try {
                await axios.delete(`/api/group-integrations/${this.groupId}/${this.connection.id}/associations/${team_id}`);
            } catch (error) {
                console.error('Error removing association:', error);
            }
        },
        async fetchAssociations() {
            try {
                const response = await axios.get(`/api/group-integrations/${this.groupId}/${this.connection.id}/associations`);
                this.associations = response.data?.integration;
            } catch (error) {
                console.error('Error fetching associations:', error);
            }
        }
    },
    watch: {
        'connection.webhooks': {
            immediate: true,
            handler(webhooks) {
                this.selectedEvents = webhooks[0]?.config?.event_types || [];
            }
        }
    }
};
</script>

<style scoped>
.card-default {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.list-group-item {
    cursor: pointer;
}
</style>